import { useEffect, useState } from "react";
import {
  fetchLeadsForRefresh,
  buildRefreshPrompt,
  getLastRefreshAt,
  applyRefreshUpdates,
  extractJsonFromText,
} from "../../lib/crm-refresh";

type Step = "generating" | "ready" | "applying" | "done";

export default function RefreshModal({
  open,
  onClose,
  onApplied,
}: {
  open: boolean;
  onClose: () => void;
  onApplied?: () => void;
}) {
  const [step, setStep] = useState<Step>("generating");
  const [prompt, setPrompt] = useState<string>("");
  const [lastRefresh, setLastRefresh] = useState<string | null>(null);
  const [pasted, setPasted] = useState<string>("");
  const [autoCopiedAt, setAutoCopiedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    leadsUpdated: number;
    actionsClosed: number;
    actionsCreated: number;
    errors: string[];
  } | null>(null);
  const [leadsCount, setLeadsCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    setStep("generating");
    setError(null);
    setResult(null);
    setPasted("");
    setAutoCopiedAt(null);
    (async () => {
      try {
        const [leads, last] = await Promise.all([
          fetchLeadsForRefresh(),
          getLastRefreshAt(),
        ]);
        setLeadsCount(leads.length);
        setLastRefresh(last);
        const p = buildRefreshPrompt(leads, last);
        setPrompt(p);
        try {
          await navigator.clipboard.writeText(p);
          setAutoCopiedAt(new Date().toLocaleTimeString("fr-FR"));
        } catch {
          // clipboard write blocked — user will need to copy manually
        }
        setStep("ready");
      } catch (e: any) {
        setError(e?.message ?? String(e));
      }
    })();
  }, [open]);

  const handleManualCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setAutoCopiedAt(new Date().toLocaleTimeString("fr-FR"));
    } catch (e: any) {
      alert("Copy bloqué par le navigateur. Sélectionne le prompt et fais Cmd+C manuellement.");
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setPasted(text);
    } catch (e: any) {
      alert(
        "Lecture du clipboard bloquée. Colle manuellement dans la zone ci-dessous (Cmd+V).",
      );
    }
  };

  const handleApply = async () => {
    setStep("applying");
    setError(null);
    try {
      const payload = extractJsonFromText(pasted);
      const r = await applyRefreshUpdates(payload);
      setResult(r);
      setStep("done");
      onApplied?.();
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setStep("ready");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="bg-charcoal text-ivory px-6 py-4 flex items-baseline justify-between">
          <h2 className="font-sans text-xl font-bold">🔄 Rafraîchir depuis Gmail + WhatsApp</h2>
          <button onClick={onClose} className="font-mono text-stone hover:text-ivory">
            ✕ Fermer
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {step === "generating" && (
            <p className="font-mono text-sm text-concrete">
              Génération du prompt en cours…
            </p>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 font-mono text-xs">
              ❌ {error}
            </div>
          )}

          {(step === "ready" || step === "applying") && (
            <>
              <div className="text-sm text-concrete space-y-1">
                <p>
                  <strong>{leadsCount} leads actifs</strong> à refresh ·{" "}
                  {lastRefresh ? (
                    <>
                      dernier refresh : <strong>{new Date(lastRefresh).toLocaleString("fr-FR")}</strong>
                    </>
                  ) : (
                    <>aucun refresh précédent · scan des 7 derniers jours</>
                  )}
                </p>
              </div>

              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li className="flex items-baseline gap-2 flex-wrap">
                  <span>Le prompt est {autoCopiedAt ? "copié dans ton clipboard" : "prêt"}.</span>
                  <button
                    onClick={handleManualCopy}
                    className="text-xs font-mono px-2 py-0.5 bg-charcoal text-ivory rounded hover:bg-black"
                  >
                    {autoCopiedAt ? `↻ Re-copier (copié à ${autoCopiedAt})` : "📋 Copier au clipboard"}
                  </button>
                </li>
                <li>
                  <strong>Ouvre Claude Code en local</strong> dans ton terminal (un onglet
                  avec MCP Gmail + WhatsApp accessibles).
                </li>
                <li>
                  <strong>Cmd+V</strong> pour coller le prompt, puis Entrée. Claude va
                  scanner tes 4 boîtes Gmail + WhatsApp (1-2 min).
                </li>
                <li>
                  Quand Claude a fini, <strong>sélectionne sa réponse JSON</strong> (le
                  bloc entre <code>```json</code> et <code>```</code>) et <strong>Cmd+C</strong>.
                </li>
                <li>
                  Reviens ici et clique <strong>"📋 Coller depuis clipboard"</strong>{" "}
                  ci-dessous, puis <strong>"Appliquer"</strong>.
                </li>
              </ol>

              <details className="bg-ivory rounded p-3">
                <summary className="cursor-pointer font-mono text-xs uppercase text-concrete">
                  Aperçu du prompt (~{prompt.length.toLocaleString("fr-FR")} caractères)
                </summary>
                <pre className="mt-2 p-2 bg-white border border-stone rounded text-[10px] font-mono overflow-x-auto max-h-60 whitespace-pre-wrap">
                  {prompt.slice(0, 2000)}
                  {prompt.length > 2000 && "\n…[tronqué pour l'affichage, le prompt complet est dans ton clipboard]"}
                </pre>
              </details>

              <div className="border-t border-stone pt-4">
                <div className="flex items-baseline justify-between mb-2">
                  <label className="font-mono text-[10px] uppercase text-concrete">
                    Réponse de Claude (JSON)
                  </label>
                  <button
                    onClick={handlePasteFromClipboard}
                    className="text-xs font-mono px-2 py-0.5 bg-charcoal text-ivory rounded hover:bg-black"
                  >
                    📋 Coller depuis clipboard
                  </button>
                </div>
                <textarea
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  rows={6}
                  placeholder='Soit clique "Coller depuis clipboard", soit Cmd+V ici. Doit contenir un bloc ```json {...} ```'
                  className="w-full px-3 py-2 bg-ivory border border-stone rounded text-xs font-mono focus:outline-none focus:border-charcoal resize-y"
                />
                {pasted.trim() && (
                  <p className="text-[10px] text-ash font-mono mt-1">
                    {pasted.trim().length} caractères collés
                  </p>
                )}
              </div>
            </>
          )}

          {step === "done" && result && (
            <div className="space-y-3">
              <div className="p-4 bg-green-50 border border-green-200 rounded">
                <p className="font-sans text-lg font-bold text-green-800 mb-2">
                  ✓ Refresh appliqué
                </p>
                <ul className="text-sm text-charcoal space-y-1">
                  <li>📩 Leads mis à jour : <strong>{result.leadsUpdated}</strong></li>
                  <li>✓ Actions auto-clôturées : <strong>{result.actionsClosed}</strong></li>
                  <li>➕ Nouvelles actions créées : <strong>{result.actionsCreated}</strong></li>
                </ul>
              </div>
              {result.errors.length > 0 && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="font-mono text-xs uppercase text-orange-800 mb-1">
                    {result.errors.length} avertissement{result.errors.length > 1 ? "s" : ""}
                  </p>
                  <ul className="text-xs text-charcoal font-mono space-y-0.5">
                    {result.errors.slice(0, 10).map((e, i) => (
                      <li key={i}>• {e}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-stone flex items-center justify-end gap-2 bg-ivory">
          {step === "ready" && (
            <button
              onClick={handleApply}
              disabled={!pasted.trim()}
              className="px-4 py-2 bg-lemonade text-charcoal font-bold rounded hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Appliquer
            </button>
          )}
          {step === "applying" && (
            <span className="font-mono text-sm text-concrete">Application en cours…</span>
          )}
          {step === "done" && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-charcoal text-ivory rounded hover:bg-black"
            >
              Fermer
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
